/**
 * Жизненный цикл переносной единицы + команды (вариант Б, решение Павла
 * 25.09.2026): сверху цикл «Склад → У клиента → Приёмка» с текущим этапом,
 * снизу — крупные кнопки команд под палец, чтобы с телефона на площадке
 * одним тапом отгрузить, вернуть, отметить поломку или выпустить.
 *
 * Сам компонент ничего не решает и никуда не ходит: команды собирает
 * карточка (EquipmentModal) из прав и статуса, каждая ведёт в уже
 * существующий механизм — диалог перемещения, форму Работы, /status.
 *
 * Ответвления цикла — «Неисправна» (у клиента) и «На обслуживании» (на
 * складе) — показаны меткой под своим этапом: это не отдельные остановки
 * маршрута, а состояние внутри этапа, которое выводится из Работ.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import BusyLabel from './BusyLabel';
import { STATUS_COLORS } from './EquipmentStatusBadge';
import { track } from '../analytics';

const STAGES = [
  { stored: 'ready', side: null, label: 'equipmentStatus.cmd.stageWarehouse', icon: 'check' },
  { stored: 'on_hire', side: 'breakdown', label: 'equipmentStatus.cmd.stageClient', icon: 'truck' },
  { stored: 'off_hire', side: 'in_service', label: 'equipmentStatus.cmd.stageCheck', icon: 'clock' },
];

// Ответвление → этап, внутри которого оно живёт. «На обслуживании» — открытая
// Работа на складе: продолжение приёмки, а не «Готова».
const SIDE_OF = { breakdown: 'on_hire', in_service: 'off_hire' };

function Stage({ stage, status, t, first, last }) {
  const active = status === stage.stored || SIDE_OF[status] === stage.stored;
  // Цвет кружка — цвет текущего статуса: «Неисправна» красит этап «У клиента».
  const c = STATUS_COLORS[active ? status : stage.stored] || STATUS_COLORS.ready;
  const sideKey = stage.side;
  const sideActive = sideKey && status === sideKey;
  const sc = sideKey ? STATUS_COLORS[sideKey] : null;
  const line = (show) => (
    <div style={{ flex: 1, height: 2, background: show ? 'var(--border)' : 'transparent' }} />
  );
  return (
    <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {line(!first)}
        <div style={{
          width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          ...(active
            ? { background: c.bg, color: c.fg, border: `1.5px solid ${c.fg}` }
            : { background: 'var(--bg2)', color: 'var(--text3)', border: '1px solid var(--border)' }),
        }}>
          <Icon name={stage.icon} size={14} />
        </div>
        {line(!last)}
      </div>
      <div style={{ fontSize: 12, marginTop: 4, fontWeight: active ? 700 : 500,
                    color: active ? c.fg : 'var(--text2)' }}>
        {t(stage.label)}
      </div>
      {/* Ответвление («Неисправна», «На обслуживании») — только когда единица
          в нём. Серой подписью «на всякий случай» оно читалось как диагноз:
          насос в аренде и исправен, а под ним написано «Неисправна». */}
      {sideActive && (
        <div style={{
          display: 'inline-block', marginTop: 4, fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 6,
          background: sc.bg, color: sc.fg,
        }}>
          {t(`equipmentStatus.${sideKey}`)}
        </div>
      )}
    </div>
  );
}

/**
 * @param {string} status   итоговый статус (effectiveStatus с сервера)
 * @param {Array<{key, label, to?, primary?, danger?, busy?, onClick}>} commands
 */
export default function EquipmentStatusFlow({ status, commands }) {
  const { t } = useTranslation();
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', padding: '6px 0 2px' }}>
        {STAGES.map((s, i) => (
          <Stage key={s.stored} stage={s} status={status} t={t} first={i === 0} last={i === STAGES.length - 1} />
        ))}
      </div>

      {commands.length > 0 && (
        <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
          {commands.map((cmd) => {
            const c = cmd.to ? STATUS_COLORS[cmd.to] : null;
            const style = { width: '100%', minHeight: 44, justifyContent: 'center', fontSize: 14 };
            if (cmd.primary && c) Object.assign(style, { background: c.bg, color: c.fg, borderColor: c.fg, fontWeight: 700 });
            else if (cmd.danger) Object.assign(style, { color: STATUS_COLORS.breakdown.fg, borderColor: 'rgba(220,38,38,0.5)' });
            return (
              <button key={cmd.key} type="button" className="btn" style={style}
                      data-ph-capture={`equipment_cmd_${cmd.key}`}
                      disabled={!!cmd.busy}
                      onClick={() => { track('equipment_cmd', { cmd: cmd.key, status }); cmd.onClick(); }}>
                <BusyLabel busy={!!cmd.busy} label={t('common.saving')} light={false}>
                  {cmd.danger && '🚨 '}{cmd.label}
                </BusyLabel>
              </button>
            );
          })}
        </div>
      )}

    </div>
  );
}
